<template>
	<v-defaults-provider :defaults="overlayDefaults">
		<v-card id="traffic-log-list" class="fill-height">
			<Toolbar :items="toolbarItems" :panelParams="params"/>
			<NoStationSelectedContainer v-if="!selectedStation"/>
			<Loader v-else :value="!trafficLogs || loading"/>
			<div id="traffic-log-list-container" class="d-flex" style="height: calc(100% - 52px);">
				<Clock v-if="selectedStation"
					:selectedStation="selectedStation"
					:selectedDateTime="datetime"
					:stationCurrentHour="stationCurrentHour"
					:tabHeight="clockTabHeight"
					@selectDateTime="getTraffic"/>
				<perfect-scrollbar>
					<v-list>
						<v-expansion-panels multiple variant="accordion" class="flex-column">
							<TrafficLogItem v-for="logEntry in trafficLogs" :key="logEntry.id"
								:selectedStation="selectedStation"
								:id="logEntry.id"
								:logEntry="logEntry"/>
						</v-expansion-panels>
					</v-list>
				</perfect-scrollbar>
			</div>
		</v-card>
	</v-defaults-provider>
</template>

<script>
/* Libraries */
import _ from "lodash";
import emitter from "@/emitter";
import moment from "moment-timezone";

/* Modules */
import {VBtn, VTooltip} from "vuetify/components";
import Clock from "@/components/Clock.vue";
import Loader from "@/components/Loader.vue";
import NoStationSelectedContainer from "@/components/NoStationSelectedContainer.vue";
import PlaylistDateTimePicker from "@/components/PlaylistDateTimePicker.vue";
import Toolbar from "@/components/Toolbar.vue";
import TrafficLogItem from "@/components/TrafficLogItem.vue";

/* Mixins */
import PanelPropertiesMixin from "@/mixins/PanelProperties";

export default {
	name: "TrafficLog",

	components: {
		Clock,
		Loader,
		NoStationSelectedContainer,
		Toolbar,
		TrafficLogItem
	},

	mixins: [PanelPropertiesMixin],

	props: ["params"],

	data() {
		return {
			selectedDateTime: moment().startOf("hour"),
			clockTabHeight: null,
			loading: false,
			trafficLogs: [],
			datePickerOpen: false,
			timePickerOpen: false,
		}
	},

	computed: {
		toolbarItems() {
			if (!this.selectedStation) {
				return [];
			}

			return [
				{
					component: PlaylistDateTimePicker,
					props: {
						datePickerOpen: this.datePickerOpen,
						timePickerOpen: this.timePickerOpen,
						selectedDateTime: this.selectedDateTime,
						loading: this.loading
					},
					actions: {
						datePickerClicked: (open) => {
							this.datePickerOpen = open;
							this.timePickerOpen = false;
						},
						onDayClicked: (date) => {
							date = date.format("YYYY-MM-DD");
							this.loadTraffic({date});
						},
						timePickerClicked: (open) => {
							this.timePickerOpen = open;
							this.datePickerOpen = false;
						},
						onTimeClicked: (hour) => {
							this.loadTraffic({hour});
							this.timePickerOpen = false;
						}
					}
				},
				{
					component: VTooltip,
					props: {
						location: "bottom",
						text: "Go to Current Hour",
						openDelay: 500
					},
					slots: [
						{
							name: "activator",
							items: (data) => [
								{
									component: VBtn,
									props: {
										...data.props,
										icon: "mdi-home-circle",
										ripple: false,
										elevation: 1,
										height: 30,
										style: {
											"border-radius": "3px",
											"margin-right": "12px",
											"width": "34px",
											"height": "34px"
										}
									},
									actions: {
										"click": () => {
											if (!_.isEqual(this.selectedDateTime, this.stationCurrentHour)) {
												this.selectedDateTime = this.stationCurrentHour;
												this.getTraffic();
											}
										}
									}
								}
							]
						}
					]
				}
			]
		},

		datetime() {
			return this.selectedDateTime || moment.tz(this.stationTimezone).startOf("hour");
		}
	},

	methods: {
		loadTraffic({date, hour}) {
			if (!date) {
				date = this.datetime.format("YYYY-MM-DD")
			}
			if (!hour) {
				hour = this.datetime.format("HH:mm");
			}

			this.getTraffic(moment.tz(`${date} ${hour}`, "YYYY-MM-DD HH:mm", this.stationTimezone));
		},

		getTraffic(dateTime) {
			if (!this.loading) {
				this.loading = true;
				this.selectedDateTime = moment.tz(dateTime, this.stationTimezone).startOf("hour");

				const startTime = this.selectedDateTime.format("YYYY-MM-DDTHH:mm:ss");
				const breakDuration = 3599;

				this.$store.dispatch("getTrafficLog", {station: this.selectedStation, startTime, breakDuration})
				    .then(response => {
						this.trafficLogs = response.data.zettaCloudResponseData.map(resData => {
							return resData.spots.map(spot => {
								return {
									id: spot.id,
									scheduledStartTime: spot.scheduledStartTime,
									displayName: spot.displayName,
									advertiserName: spot.advertiserName,
									blockNumber: spot.blockNumber,
									spotLength: spot.spotLength
								}
							})
						}).reduce((acc, spot) => acc.concat(spot), []);

						this.trafficLogs.sort((a, b) => {
							return moment(a.scheduledStartTime, "YYYY-MM-DD HH:mm:ss.S").diff(
								moment(b.scheduledStartTime, "YYYY-MM-DD HH:mm:ss.S")
							);
						});
					})
				    .finally(() => this.loading = false)
			}
		},

		setClockTabHeight() {
			const container = document.getElementById("traffic-log-list-container");
			if (container) {
				this.clockTabHeight = container.clientHeight / 24;
			}
		}
	},

	mounted() {
		this.selectedDateTime = moment.tz(this.stationTimezone).startOf("hour");
		this.$nextTick(this.setClockTabHeight);
		if (this.selectedStation) {
			this.getTraffic();
		}

		emitter.on("docking:layoutChanged", this.setClockTabHeight);

		window.addEventListener("resize", this.setClockTabHeight);
	},

	beforeUnmount() {
		emitter.off("docking:layoutChanged", this.setClockTabHeight);
		window.removeEventListener("resize", this.setClockTabHeight);
	}
}
</script>

<style lang="scss" scoped>
</style>